import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { get, minBy, sortBy, flow } from 'lodash-es';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
  InventoryTrimSummaryEntities,
} from 'client/data/models/inventory-core-configurations';
import {
  buildTrimSummariesCoreConfigurationVehicles,
  calculateEstimatedSavingsConfiguration,
} from 'site-modules/shared/utils/core-page/core-configurations';
import { buildRecommendedTrimPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import {
  CORE_PRICING_CAROUSEL_EVENTS,
  CorePricingCarouselEngagementHandler,
} from 'client/engagement-handlers/core-pricing-carousel-engagement-handler/core-pricing-carousel-engagement-handler';
import { EventToolbox } from 'client/utils/event-toolbox';
import { EngagementHandler } from 'client/engagement-handlers';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getSubmodelIdentifier } from 'site-modules/shared/utils/core-page/page-helpers';
import { getBuildAndPriceLink } from 'site-modules/shared/utils/build-and-price-link-constructor';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { getEspLabel, getSuggestedDisplayPrice } from 'site-modules/shared/utils/core-page/suggested-price';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';
import { Link } from 'site-modules/shared/components/link/link';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { PricingHeader } from 'site-modules/shared/components/core-page/pricing-header/pricing-header';
import { CARD_HEIGHT, MAX_TRIMS_COUNT, TrimBarsList } from './trim-bars-list';

import './trim-pricing-bars.scss';

EngagementHandler.register(CorePricingCarouselEngagementHandler);

const TOTAL_CARD_HEIGHT = CARD_HEIGHT + 16; // card height + margin
const LAST_CARD_HEIGHT = 30;

export function TrimPricingBarsUI({
  params,
  vehicle,
  visitorLocation = null,
  className = null,
  barClassName,
  trimSummaries = null,
  trimsFeatures = null,
  recommendedTrim = null,
  pageNameLegacy = 'null',
  leadFormName = LEAD_FORMS_CREATIVE_ID.PRICING_CARDS_FORM,
  leadFormOptions = null,
  creativeId = null,
  isMobile = false,
  pricingAdPos = null,
  isInternational = false,
  hidePricingAd = false,
  isSeot5678Enabled = false,
}) {
  const [leadFormData, setLeadFormData] = useState(null);
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle(false);
  const [currentTrimSummaries, setCurrentTrimSummaries] = useState(trimSummaries);
  const isInitialLoad = useRef(true);
  const containerRef = useRef();

  useEffect(() => {
    if (!containerRef.current || !trimSummaries) {
      return;
    }

    EventToolbox.fireCustomEvent(
      isInitialLoad.current ? CORE_PRICING_CAROUSEL_EVENTS.INITIAL_LOAD : CORE_PRICING_CAROUSEL_EVENTS.DATA_UPDATE,
      {
        coreConfigurations: trimSummaries,
        previousCoreConfigurations: currentTrimSummaries,
        leadFormDealerCount: !!get(trimSummaries, '[0].hasLeadFormDealers'),
        previousLeadFormDealerCount: !!get(currentTrimSummaries, '[0].hasLeadFormDealers'),
      },
      containerRef.current
    );

    isInitialLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimSummaries]);

  useEffect(() => {
    if (trimSummaries) {
      // Intentional synchronization of asynchronously loaded summaries.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentTrimSummaries(trimSummaries);
    }
  }, [trimSummaries]);

  const displayLeadForm = useCallback(
    ({
      currentTarget: {
        dataset: { trim: dataTrim },
      },
    }) => {
      const coreConfiguration = currentTrimSummaries.find(({ trim }) => trim === dataTrim) || currentTrimSummaries[0];
      const { id, trim, suggestedPrice, totalMsrp, baseMsrp } = coreConfiguration;

      setLeadFormData({
        params: {
          styleId: id,
          trim,
        },
        options: {
          coreConfiguration,
          bestDealPrice: suggestedPrice || totalMsrp || baseMsrp,
        },
      });
      toggleLeadFormOpen();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTrimSummaries]
  );

  if (!currentTrimSummaries?.length) {
    return null;
  }

  const currentCreativeId = creativeId || `new-pricing_module-${pageNameLegacy}`;
  const estimatedSavingsTrim = calculateEstimatedSavingsConfiguration(currentTrimSummaries);

  const currentTrim = minBy(currentTrimSummaries, trim => get(trim, 'pricing.totalMsrp', 0));
  const currentMsrp = get(currentTrim, 'totalMsrp', 0);
  const currentEsp = get(currentTrim, 'suggestedPrice', 0);
  const currentNationalBasePriceTmv = get(currentTrim, 'nationalBasePriceTmv', 0);
  const currentShowLeadForm = !!get(currentTrim, 'hasLeadFormDealers');
  const espLabel = getEspLabel({
    totalMsrp: currentMsrp,
    suggestedPrice: currentEsp,
    nationalBasePriceTmv: currentNationalBasePriceTmv,
  });

  const {
    model: { name: modelName },
    submodels: { name: submodelName },
  } = vehicle;
  const submodelIdentifier = getSubmodelIdentifier(submodelName, modelName);

  let sharedCtaProps = {
    onClick: displayLeadForm,
    'data-trim': currentTrimSummaries?.[0]?.trim,
    children: 'Compare dealer prices',
    'data-tracking-id': 'pricing_bar_shared_pricing_cta',
  };
  if (!currentShowLeadForm && !isInternational) {
    sharedCtaProps = {
      tag: Link,
      children: 'What should I pay',
      'data-tracking-id': get(currentTrim, 'inventoryCount')
        ? 'pricing_bar_shared_srp_cta'
        : 'pricing_bar_shared_configure_cta',
      to: get(currentTrim, 'inventoryCount')
        ? srpLinkBuilder({
            make: params.make,
            model: params.model,
            submodel: params.submodel,
            year: params.year,
            inventorytype: INVENTORY_TYPES_LOWERCASE.NEW,
            submodelid: params.submodel && submodelIdentifier,
            trim: makeNiceName(get(currentTrim, 'trim', '')),
          })
        : getBuildAndPriceLink(params),
    };
  }

  const leadFormCreativeId = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy,
    formName: leadFormName,
  });

  const trimBarsListProps = {
    params,
    vehicle,
    submodelIdentifier,
    recommendedTrim,
    displayLeadForm,
    barClassName,
    isMobile,
    pricingAdPos,
    isInternational,
    hidePricingAd,
    trimsFeatures,
    isSeot5678Enabled,
  };

  const trimsList = <TrimBarsList {...trimBarsListProps} trimSummaries={currentTrimSummaries} />;

  return (
    <div
      className={classnames('trim-pricing-bars', className)}
      data-tracking-parent={currentCreativeId}
      ref={containerRef}
    >
      {!isMobile && <ScrollElement id="scrollTop-pricing" />}
      <PricingHeader
        className="mb-1_5"
        params={params}
        visitorLocation={visitorLocation}
        vehicle={vehicle}
        noPricingData={!currentEsp}
        estimatedSavingsTrim={estimatedSavingsTrim}
        isInternational={isInternational}
      />
      <p className="heading-4 fw-medium text-center mb-1_5">{currentEsp ? espLabel : `${espLabel} by trim`}</p>
      <LoadingSpinner classes="mb-2" isLoading={!trimSummaries}>
        {currentTrimSummaries.length > MAX_TRIMS_COUNT ? (
          <ContentCollapse
            minHeight={`${MAX_TRIMS_COUNT * TOTAL_CARD_HEIGHT + LAST_CARD_HEIGHT}px`}
            collapseClassName="overflow-hidden-focusable pos-r"
            btnClasses="px-0_5 py-0 medium text-primary-darker"
            btnContainerClasses="py-0 mt-0_25"
            showBtnBelow
            btnTextClasses="fw-normal"
            textOpen="See fewer trims"
            textClosed="See all trims"
            btnTrackingId="pricing_module"
            btnIgnoreTrackingValue={false}
            resetParams={{
              offset: SCROLL_OFFSET,
              duration: SCROLL_DURATION,
              to: 'scrollTop-pricing',
            }}
          >
            {trimsList}
          </ContentCollapse>
        ) : (
          trimsList
        )}
      </LoadingSpinner>
      <div className="text-center">
        <Button
          color="blue-50"
          size="lg"
          className="shared-cta size-16 px-1 py-0_5 text-transform-none fw-normal"
          data-tracking-id="pricing_bar_shared_cta"
          {...sharedCtaProps}
        >
          {sharedCtaProps.children}
        </Button>
      </div>
      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormOpen}
        onDrawerToggle={toggleLeadFormOpen}
        params={{
          ...get(leadFormData, 'params'),
          vehicle,
          zipCode: get(visitorLocation, 'zipCode'),
          city: get(visitorLocation, 'city'),
        }}
        options={{
          isMobile,
          ...leadFormOptions,
          ...get(leadFormData, 'options'),
          creativeId: leadFormCreativeId,
        }}
        creativeId={leadFormCreativeId}
      />
    </div>
  );
}

TrimPricingBarsUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  visitorLocation: VisitorEntities.Location,
  className: PropTypes.string,
  barClassName: PropTypes.string,
  trimSummaries: InventoryTrimSummaryEntities.TrimSummaries,
  trimsFeatures: PropTypes.objectOf(PropTypes.string),
  recommendedTrim: PropTypes.string,
  pageNameLegacy: PropTypes.string,
  leadFormName: PropTypes.string,
  leadFormOptions: PropTypes.shape({}),
  creativeId: PropTypes.string,
  isMobile: PropTypes.bool,
  isInternational: PropTypes.bool,
  pricingAdPos: PropTypes.string,
  hidePricingAd: PropTypes.bool,
  isSeot5678Enabled: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  visitorLocation: get(state, 'visitor.location', {}),
  pageNameLegacy: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const TrimPricingBars = connect(mapStateToProps)(
  connectToModel(TrimPricingBarsUI, {
    trimSummaries: bindToPath(
      ({ vehicle }) => buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
      InventoryCoreConfigurationsModel,
      flow(buildTrimSummariesCoreConfigurationVehicles, trims =>
        sortBy(trims, ({ totalMsrp, suggestedPrice, baseMsrp, nationalBasePriceTmv }) =>
          getSuggestedDisplayPrice({ totalMsrp, suggestedPrice, baseMsrp, nationalBasePriceTmv })
        )
      )
    ),
    recommendedTrim: bindToPath(({ params }) => buildRecommendedTrimPath(params), EditorialReviewModel),
  })
);
